const normalizeSearchText = (value: string): string =>
  value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();

const isWithinEditDistance = (first: string, second: string, maximumDistance: number): boolean => {
  if (Math.abs(first.length - second.length) > maximumDistance) {
    return false;
  }

  let previousRow = Array.from({length: second.length + 1}, (_, index) => index);

  for (let firstIndex = 1; firstIndex <= first.length; firstIndex += 1) {
    const currentRow = [firstIndex];
    let smallestDistance = currentRow[0];

    for (let secondIndex = 1; secondIndex <= second.length; secondIndex += 1) {
      const substitutionCost = first[firstIndex - 1] === second[secondIndex - 1] ? 0 : 1;
      const distance = Math.min(
        currentRow[secondIndex - 1] + 1,
        previousRow[secondIndex] + 1,
        previousRow[secondIndex - 1] + substitutionCost
      );
      currentRow.push(distance);
      smallestDistance = Math.min(smallestDistance, distance);
    }

    if (smallestDistance > maximumDistance) {
      return false;
    }
    previousRow = currentRow;
  }

  return previousRow[second.length] <= maximumDistance;
};

const tokenMatches = (queryToken: string, candidateToken: string): boolean => {
  if (candidateToken.includes(queryToken)) {
    return true;
  }

  const maximumDistance = queryToken.length >= 7 ? 2 : queryToken.length >= 4 ? 1 : 0;
  if (maximumDistance === 0) {
    return false;
  }

  const shortestPrefixLength = Math.max(1, queryToken.length - maximumDistance);
  const longestPrefixLength = Math.min(candidateToken.length, queryToken.length + maximumDistance);

  for (
    let prefixLength = shortestPrefixLength;
    prefixLength <= longestPrefixLength;
    prefixLength += 1
  ) {
    if (isWithinEditDistance(queryToken, candidateToken.slice(0, prefixLength), maximumDistance)) {
      return true;
    }
  }

  return false;
};

export const matchesKnowledgeSearch = (query: string, values: string[]): boolean => {
  const queryTokens = normalizeSearchText(query).split(/\s+/).filter(Boolean);
  if (queryTokens.length === 0) {
    return true;
  }

  const candidateTokens = normalizeSearchText(values.join(' ')).split(/\s+/).filter(Boolean);
  return queryTokens.every((queryToken) =>
    candidateTokens.some((candidateToken) => tokenMatches(queryToken, candidateToken))
  );
};
